import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getReportHistory, 
  getFavoriteReports, 
  deleteReport, 
  toggleReportFavorite 
} from '../../../services/history.service';
import { showSuccess, showError } from '../../../services/notification.service';
import ConfirmDialog from '../../shared/ConfirmDialog';

const ReportHistory = ({ showFavorites = false }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, reportId: null });
  const navigate = useNavigate();

  const getModelDisplayName = (modelId) => {
    if (!modelId) return 'Default';
    
    // Extract model name from ID (e.g., "us.anthropic.claude-3-7-sonnet..." -> "Claude 3.7 Sonnet")
    if (modelId.includes('claude-3-7-sonnet')) return 'Claude 3.7 Sonnet';
    if (modelId.includes('claude-3-7')) return 'Claude 3.7';
    if (modelId.includes('claude-3-5-sonnet')) return 'Claude 3.5 Sonnet';
    if (modelId.includes('claude-3-5')) return 'Claude 3.5';
    if (modelId.includes('claude-3-haiku')) return 'Claude 3 Haiku';
    if (modelId.includes('claude-3')) return 'Claude 3';
    if (modelId.includes('claude-4')) return 'Claude 4';
    if (modelId.includes('nova-pro')) return 'Nova Pro';
    if (modelId.includes('nova-lite')) return 'Nova Lite';
    if (modelId.includes('nova-micro')) return 'Nova Micro';
    if (modelId.includes('llama-3-3')) return 'Llama 3.3';
    if (modelId.includes('llama-3-2')) return 'Llama 3.2';
    if (modelId.includes('llama')) return 'Llama';
    if (modelId.includes('deepseek')) return 'DeepSeek';
    if (modelId.includes('qwen')) return 'Qwen';
    
    // Fallback: try to extract readable name from ID
    const parts = modelId.split('.');
    const lastPart = parts[parts.length - 1] || modelId;
    return lastPart.split('-').slice(0, 2).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  useEffect(() => {
    fetchReportHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFavorites]);

  const fetchReportHistory = async () => {
    try {
      setLoading(true);
      const data = showFavorites 
        ? await getFavoriteReports()
        : await getReportHistory();
      
      if (data.success) {
        // Deduplicate reports by _id (backend might return duplicates)
        const uniqueReports = Array.from(
          new Map(data.reports.map(report => [report._id, report])).values()
        );
        
        setReports(uniqueReports);
      } else {
        setError(`Failed to fetch ${showFavorites ? 'favorite' : ''} reports`);
      }
    } catch (err) {
      setError(`Error loading ${showFavorites ? 'favorite' : ''} reports`);
      console.error('Error fetching reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectReport = async (reportId) => {
    // Navigate to the report page
    navigate(`/tools/idea-analyzer/reports/${reportId}`);
  };

  const handleDeleteReport = async (reportId, event) => {
    event.stopPropagation();
    setConfirmDialog({ isOpen: true, reportId: reportId });
  };

  const confirmDeleteReport = async () => {
    const reportId = confirmDialog.reportId;
    setConfirmDialog({ isOpen: false, reportId: null });

    try {
      const data = await deleteReport(reportId);
      
      if (data.success) {
        setReports(reports.filter(report => report._id !== reportId));
        showSuccess('Report deleted successfully');
      } else {
        const errorMsg = 'Failed to delete report';
        setError(errorMsg);
        showError(errorMsg);
      }
    } catch (err) {
      const errorMsg = 'Error deleting report';
      setError(errorMsg);
      showError(errorMsg);
      console.error('Error deleting report:', err);
    }
  };

  const handleToggleFavorite = async (reportId, currentStatus, event) => {
    event.stopPropagation();
    
    try {
      const data = await toggleReportFavorite(reportId, !currentStatus);
      
      if (data.success) {
        // Update the local state
        setReports(reports.map(report => 
          report._id === reportId 
            ? { ...report, is_favorite: !currentStatus }
            : report
        ).filter(report => !showFavorites || report.is_favorite));
        
        showSuccess(!currentStatus ? 'Added to favorites' : 'Removed from favorites');
      } else {
        const errorMsg = 'Failed to update favorite status';
        setError(errorMsg);
        showError(errorMsg);
      }
    } catch (err) {
      const errorMsg = 'Error updating favorite status';
      setError(errorMsg);
      showError(errorMsg);
      console.error('Error updating favorite:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="report-history">
        <div className="history-header">
          <h2>📚 Report History</h2>
          <button onClick={() => navigate('/')} className="new-report-btn">
            ✨ New Report
          </button>
        </div>
        <div className="loading-container">
          <div className="spinner-large"></div>
          <p>Loading your reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="report-history">
      <div className="history-header">
        <h2>{showFavorites ? '⭐ Favorite Reports' : '📚 Report History'}</h2>
        <button onClick={() => navigate('/')} className="new-report-btn">
          ✨ New Report
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={fetchReportHistory} className="retry-btn">
            🔄 Retry
          </button>
        </div>
      )}

      {reports.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">{showFavorites ? '⭐' : '📋'}</div>
          <h3>{showFavorites ? 'No Favorite Reports' : 'No Reports Yet'}</h3>
          <p>{showFavorites ? 'Mark reports as favorites to see them here' : 'Generate your first analysis report to see it here'}</p>
          <button onClick={() => navigate('/')} className="generate-first-btn">
            🚀 Generate {showFavorites ? 'New' : 'First'} Report
          </button>
        </div>
      ) : (
        <div className="reports-grid">
          {reports.map((report) => (
            <div
              key={report._id}
              className="report-card"
              onClick={() => handleSelectReport(report._id)}
            >
              <div className="report-card-header">
                <h3 className="report-title">{report.title}</h3>
                <div className="report-actions">
                  <button
                    className={`favorite-btn ${report.is_favorite ? 'active' : ''}`}
                    onClick={(e) => handleToggleFavorite(report._id, report.is_favorite, e)}
                    title={report.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    {report.is_favorite ? '⭐' : '☆'}
                  </button>
                  <button
                    className="delete-btn"
                    onClick={(e) => handleDeleteReport(report._id, e)}
                    title="Delete Report"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              
              <div className="report-meta">
                <span className="report-mode">
                  {report.mode === 'advanced' ? '🚀 Advanced' : '📋 Standard'}
                </span>
                <span className="report-backend">
                  {report.backend_type === 'strands' ? '🤖 Multi-Agent' : 
                   report.backend_type === 'mcp' ? '🔌 MCP' : '⚡ Traditional'}
                </span>
                <span className="report-sections">
                  📄 {report.total_sections} sections
                </span>
              </div>

              {/* NEW: Generation Settings */}
              {report.generation_settings && (
                <div className="report-settings">
                  {report.generation_settings.model_id && (
                    <span className="setting-badge model-badge" title={report.generation_settings.model_id}>
                      🤖 {getModelDisplayName(report.generation_settings.model_id)}
                    </span>
                  )}
                  {report.generation_settings.questionnaire_mode && (
                    <span className="setting-badge questionnaire-badge" title="8-Question Mode">
                      📋 8Q
                    </span>
                  )}
                  {report.generation_settings.workflow_type && (
                    <span className="setting-badge workflow-badge" title={`Workflow: ${report.generation_settings.workflow_type}`}>
                      🔗 {report.generation_settings.workflow_type}
                    </span>
                  )}
                </div>
              )}

              <div className="report-stats">
                <div className="confidence-mini">
                  <span className="confidence-label">Confidence:</span>
                  <div className="confidence-dots">
                    {report.confidence_levels && (
                      <>
                        <span className="confidence-dot high" title={`High: ${report.confidence_levels.High}`}>
                          {report.confidence_levels.High}
                        </span>
                        <span className="confidence-dot medium" title={`Medium: ${report.confidence_levels.Medium}`}>
                          {report.confidence_levels.Medium}
                        </span>
                        <span className="confidence-dot low" title={`Low: ${report.confidence_levels.Low}`}>
                          {report.confidence_levels.Low}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                
                {report.research_papers_count > 0 && (
                  <div className="research-count">
                    📚 {report.research_papers_count} references
                  </div>
                )}
              </div>

              <div className="report-date">
                <span>Generated: {formatDate(report.created_at || report.generated_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title="Delete Report"
        message="Are you sure you want to delete this report? This action cannot be undone."
        type="danger"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDeleteReport}
        onCancel={() => setConfirmDialog({ isOpen: false, reportId: null })}
      />
    </div>
  );
};

export default ReportHistory;